import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import omit from 'lodash/omit';
import mapKeys from 'lodash/mapKeys';

import {Alignment, AlignmentOptions} from 'common/widgets/sankey-chart/models/types';
import {ResourceType} from 'common/widgets/property-filter-menu/models/types';
import {getSegmentType, isCohortSegment} from 'common/widgets/query-block/segment-menu/types';
import {FullLengthDateRange} from 'common/widgets/date-range-picker/models/full-length-date-range';
import {QueryEventEntry} from 'common/widgets/query-entry-section/types';
import {EventItem} from 'common/widgets/query-block/events-menu/types';
import {PropertyItem} from 'common/widgets/query-block/properties-menu/types';

import {DEFAULT_VALUES as FUNNELS_BOOKMARK_DEFAULT_VALUES, FunnelsBookmark} from '../funnels/bookmark';
import {CHART_TYPE_TO_QUERY_PARAM, ChartType, FlowAnchorPosition} from './types';

export const FLOWS_REPORT_DEFAULTS = Object.freeze({
  alignment: [Alignment.RIGHT, Alignment.LEFT],
  alignBreakdowns: true,
  alwaysBreakdown: false,
  anchorPosition: FlowAnchorPosition.Step,
  binaryTag: null,
  cardinality: 3,
  chartType: ChartType.Sankey,
  cohortFilters: [],
  collapseRepeated: false,
  graphSortPriority: `absolute`,
  groupBy: [],
  hiddenEvents: [],
  id: null,
  listSortPriority: `end`,
  maxResults: 50,
  maxSteps: -1,
  name: null,
  description: null,
  relativeBreakdowns: false,
  reverse: false,
  segments: [],
  timePercentiles: [],
  title: null,
  uniqueFlows: false,
  userId: null,
  userName: null,
  userEmail: null,
  version: `1`,
  visibleEvents: [],

  // permissions
  can_view: false,
  can_update_basic: false,
  can_share: false,
  is_superadmin: false,
  allow_staff_override: false,
});

export const BOOKMARK_PARAM_MAP = Object.freeze({
  collapse_repeated: `collapseRepeated`,
  hidden_events: `hiddenEvents`,
  visible_events: `visibleEvents`,
  graph_sort_priority: `graphSortPriority`,
  list_sort_priority: `listSortPriority`,
  segments: `segments`,
});

const BOOKMARK_COPY_OVER_KEYS = Object.freeze([
  `alignment`,
  `anchorPosition`,
  `maxSteps`,
  `maxResults`,
  `cardinality`,
  `chartType`,
  `reverse`,
]);

const FLOWS_REPORT_DEFAULTS_KEYS = Object.keys(FLOWS_REPORT_DEFAULTS);
const FUNNELS_BOOKMARK_DEFAULTS_KEYS = Object.keys(FUNNELS_BOOKMARK_DEFAULT_VALUES);

const BOOKMARK_PARAM_MAP_KEYS = Object.keys(BOOKMARK_PARAM_MAP);
const DEFAULTS = {...FLOWS_REPORT_DEFAULTS, ...FUNNELS_BOOKMARK_DEFAULT_VALUES};
const URL_PARAMS = Object.keys(DEFAULTS);

export enum AnchorPosition {
  Funnel,
  StepAndFunnel,
}

export const CARDINALITY_FOR_DOWNLOAD = 5;
export const MAX_STEPS_FOR_DOWNLOAD = 5;
export const MAX_RESULTS_FOR_DOWNLOAD = 3;

export class FlowsReport extends FunnelsBookmark {
  public static fromBookmark(bookmark) {
    const {
      params,
      name,
      id,
      userId,
      userName,
      userEmail,
      description,

      /* eslint-disable @typescript-eslint/naming-convention */
      // permissions
      can_view,
      can_update_basic,
      can_share,
      is_superadmin,
      allow_staff_override,
      /* eslint-enable */
    } = bookmark;

    if (!isNaN(params.anchorPosition)) {
      params.anchorPosition = Object.values(FlowAnchorPosition)[params.anchorPosition];
    }

    const funnelsBookmarkParams = FunnelsBookmark.bookmarkToClassParams(params);
    const convertedParams = mapKeys(pick(params, BOOKMARK_PARAM_MAP_KEYS), (_, k) => BOOKMARK_PARAM_MAP[k]);
    const copiedParams = pick(params, BOOKMARK_COPY_OVER_KEYS);

    const attrs = {
      ...copiedParams,
      ...funnelsBookmarkParams,
      ...convertedParams,
      name,
      id,
      userId,
      userName,
      userEmail,
      description,

      // permissions
      can_view,
      can_update_basic,
      can_share,
      is_superadmin,
      allow_staff_override,
    };

    return new FlowsReport(attrs);
  }

  // @ts-expect-error TS2611: 'events' is defined as a property in class 'FunnelsBookmark', but is overridden here in 'FlowsReport' as an accessor.
  get events() {
    return this._events;
  }

  set events(events: QueryEventEntry[]) {
    // The current implementation for alignment works for the toggle all alignment implementation.
    // If we later switch to controling the alignment per section, this implementation will have to be revisited.
    if (!this.alignment || this.alignment.length === 0) {
      // Reverse steps of the first anchor are always right aligned.
      this.alignment = [Alignment.RIGHT].concat(new Array<Alignment>(events.length).fill(Alignment.LEFT));
    } else if (events.length > 0 && events.length !== this.alignment.length - 1) {
      // Forward steps of the last anchor are always left aligned.
      const alignmentFill = this.getOverallAlignment();
      this.alignment = [Alignment.RIGHT]
        .concat(new Array<Alignment>(events.length - 1).fill(alignmentFill))
        .concat([Alignment.LEFT]);
    }
    this._events = this.generateFlowsEvents(events);
  }

  get eventSelection() {
    return this.events.filter(Boolean);
  }

  get cohortGroupBys() {
    return this.groupBy.filter(isCohortSegment);
  }

  get trackingParams() {
    return {
      'Anchor Events': this.eventSelection.map((entry: QueryEventEntry) => entry.event.label),
      'Custom event': this.eventSelection.some((entry: QueryEventEntry) => entry.event.custom),
      'Event type': this.eventSelection.map((entry: QueryEventEntry) =>
        entry.event.custom ? `Custom event` : `Event`,
      ),
      'Tab name': this.chartType,
      reverse: !!this.reverse,
      '# of filters': this.filters.length,
      '# of breakdowns': this.segments.length,
      '# of hidden events': this.hiddenEvents.length,
      '# of visible events': this.visibleEvents.length,
      '# of cohort filters': this.cohortFilters.length,
      '# of cohort segments': this.groupBy.map((segment) => getSegmentType(segment) === ResourceType.Cohort).length,
      'Query time Sampling': this.querySampling,
      Alignment: AlignmentOptions[this.getOverallAlignment()].label,
      '# of steps': this.eventSelection
        .map((event) => (event[`forward`] || 0) + (event[`reverse`] || 0))
        .reduce((acc, val) => acc + val, 0),
      '# of anchor events': this.eventSelection.length,
      'Time percetiles': this.timePercentiles.length,
      'Collapse Repeated': this.collapseRepeated,
      ...(this.maxSteps === -1 ? {'Flows Version': `anchor steps`} : {}),
    };
  }
  public alignment: Alignment[];
  public anchorPosition: string;
  public chartType: ChartType;
  public collapseRepeated: boolean;
  public graphSortPriority: string;
  public listSortPriority: string;
  public hiddenEvents: string[];
  public visibleEvents: string[];
  public reverse: boolean;
  public cardinality: number;
  public maxSteps: number;
  // TODO: change timePercentiles to a boolean when Flows API is dropped
  public timePercentiles: number[];
  public maxResults: number;
  public relativeBreakdowns: boolean;
  public segments: Array<{
    event: EventItem;
    property: PropertyItem;
  }>;
  private _events: QueryEventEntry[];

  constructor(attrs = {}) {
    super(attrs);
    Object.assign(this, FLOWS_REPORT_DEFAULTS);
    Object.assign(this, pick(attrs, FLOWS_REPORT_DEFAULTS_KEYS));
  }

  public clone(attrs: Partial<FlowsReport> = {}) {
    return new FlowsReport({
      ...pick(this, [...FLOWS_REPORT_DEFAULTS_KEYS, ...FUNNELS_BOOKMARK_DEFAULTS_KEYS]),
      ...attrs,
    });
  }

  /**
   * Calculate which params are non-default, and serialize any
   * complex values (like dateRange)
   */
  public toUrlData() {
    const urlParams = URL_PARAMS.reduce((params, key) => {
      if (
        key !== `dateRange` ? !isEqual(this[key], DEFAULTS[key]) : !isEqual(this[key]?.getAttributes(), DEFAULTS[key])
      ) {
        params[key] = this[key];
      }
      return params;
    }, {});

    return isEmpty(urlParams)
      ? null
      : {
          ...urlParams,
          dateRange: this.dateRange.getAttributes(),
          version: this.version,
        };
  }

  public paramEquals(param, value) {
    switch (param) {
      case `dateRange`:
        return this.dateRange.equals(value);
      case `events`:
        return isEqual(this.events, this.generateFlowsEvents(value));
      default:
        return this[param] === value;
    }
  }

  public update(params: Partial<FlowsReport> = {}): boolean {
    let changed = false;

    for (const [k, v] of Object.entries(params)) {
      if (!this.paramEquals(k, v)) {
        changed = true;
        this[k] = v;
      }
    }

    if (`dateRange` in params) {
      this.dateRange = new FullLengthDateRange(params.dateRange);
    }

    // updating maxSteps requires re-generating the
    // events structure as it is derived from maxSteps
    if (`maxSteps` in params || `reverse` in params) {
      // eslint-disable-next-line no-self-assign
      this.events = this.events;
    }

    return changed;
  }

  public generateBookmark(): any {
    const includeNegatedCohort = this.cohortGroupBys.length > 0 && this.cohortGroupBys[0].length === 1;
    const funnelsBookmark = super.generateBookmark({includeNegatedCohort});
    const bookmark = {
      ...omit(funnelsBookmark, `length_unit`, `length`),
      anchorPosition: this.anchorPosition,
      cardinality_threshold: this.chartType === ChartType.Sankey ? this.cardinality : this.maxResults,
      flows_merge_type: CHART_TYPE_TO_QUERY_PARAM[this.chartType],
      segments: this.segments,
    };

    if (this.anchorPosition === FlowAnchorPosition.Funnel) {
      bookmark[`anchor_position`] = AnchorPosition.Funnel;
    } else {
      bookmark[`anchor_position`] = AnchorPosition.StepAndFunnel;
    }

    if (this.visibleEvents.length) {
      bookmark[`visible_events`] = this.visibleEvents;
    } else {
      bookmark[`hidden_events`] = this.hiddenEvents;
    }

    if (this.chartType === ChartType.Sankey) {
      bookmark[`graph_sort_priority`] = this.graphSortPriority;
    } else {
      bookmark[`list_sort_priority`] = this.listSortPriority;
    }

    if (this.timePercentiles.length) {
      bookmark[`time_percentiles_enabled`] = true;
    }

    if (this.cohortGroupBys.length) {
      if (this.relativeBreakdowns) {
        bookmark[`graph_sort_priority`] = `relative`;
      }

      if (this.chartType === ChartType.Sankey) {
        bookmark[`exclude_everything_else_user_segment`] = `true`;
      }
    }

    bookmark[`collapse_repeated`] = this.collapseRepeated;

    Object.assign(bookmark, pick(this, BOOKMARK_COPY_OVER_KEYS));
    return bookmark;
  }

  /**
   * Truncates left to right to create a basic preview query for download mode
   */
  public truncateForDownload() {
    let allowedSteps = MAX_STEPS_FOR_DOWNLOAD - this.events.length;
    const events = this.events.map((event) => {
      if (allowedSteps > 0) {
        const {forward = 0, reverse = 0} = event;
        if (allowedSteps - forward - reverse >= 0) {
          allowedSteps -= forward + reverse;
          return event;
        } else {
          const truncatedReverse = Math.min(reverse, allowedSteps);
          allowedSteps -= truncatedReverse;
          const truncatedForward = Math.min(forward, allowedSteps);
          allowedSteps -= truncatedForward;

          return {...event, forward: truncatedForward, reverse: truncatedReverse};
        }
      }
      return {...event, forward: 0, reverse: 0};
    });
    const cardinality = Math.min(this.cardinality, CARDINALITY_FOR_DOWNLOAD);
    const maxResults = Math.min(this.maxResults, MAX_RESULTS_FOR_DOWNLOAD);

    return this.clone({maxSteps: -1, events, cardinality, maxResults});
  }

  /**
   * Generates events with forward and reverse fields
   */
  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  private generateFlowsEvents(events: QueryEventEntry[]): QueryEventEntry[] {
    // Setting maxSteps to -1 disables the legacy way of controlling steps and lets the events themselves control
    // the forward and reverse steps independently.
    if (this.maxSteps === -1) return events;

    const maxSteps = this.chartType === ChartType.Paths ? 5 : this.maxSteps || 3;
    // handle non-beta single event anchorSelection
    if (events.length === 1 && events[0]) {
      const entry = omit(events[0], `forward`, `reverse`) as QueryEventEntry;
      return [
        {
          ...entry,
          event: entry.event,
          [this.reverse ? `reverse` : `forward`]: maxSteps,
        },
      ];
    }

    // fill left to right direction for all anchors except last
    // allows for setting reverse flows without explicitly
    // defining the `reverse` property
    const isSparse = events.some((entry) => isEmpty(entry));

    return events.map((entry, index) => {
      if (isEmpty(entry)) {
        return null;
      }

      entry = {
        ...(omit(entry, `forward`, `reverse`) as QueryEventEntry),
        event: entry.event,
      };

      if (this.reverse) {
        entry[`reverse`] = maxSteps;
      } else if (index === events.length - 1) {
        entry[`reverse`] = isSparse ? maxSteps : 0;
      } else {
        entry[`forward`] = maxSteps;
      }

      return entry;
    });
  }

  public getOverallAlignment() {
    return this.alignment && this.alignment.length >= 2 ? this.alignment[1] : Alignment.LEFT;
  }
}
